import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  BarChart3,
  Phone,
  Users,
  FileText,
  Briefcase,
  Handshake,
  Info,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { MappingIncompleteBanner } from '../../components/broker-activity/MappingIncompleteBanner';
import { useWeeklyFunnel, WeeklyFunnelRow } from '../../hooks/useWeeklyFunnel';

type OutletCtx = {
  userData?: { companyId?: string; role?: string; canSeeAllOffices?: boolean };
};

// Phase 1.5 B.5 — Weekly Funnel for the Broker Activity subapp.
// Per-broker per-ISO-week conversion: Calls → Contacts → HelloPacks →
// Lodged → Settled. Mirrors the layout of ScorecardPage / PipelinePage.

const WEEKS_OPTIONS = [4, 8, 12, 26] as const;

/**
 * Rendered instead of a count when the backing source holds no rows for this
 * tenant. A 0 there would assert "this team did none of these" when the truth
 * is "this team does not record these" — the same false statement the backend
 * returns NULL rather than 0 for on matureConversionPct.
 */
const NOT_TRACKED = '—';
const NOT_TRACKED_HINT =
  'Not tracked: nothing has been recorded for this stage yet, so there is no number to show.';

function formatWeekLabel(weekStart: string): string {
  if (!weekStart) return '—';
  try {
    const d = new Date(weekStart);
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return weekStart;
  }
}

/**
 * Resolve a display label for a funnel row. After the backend identity
 * bridge the value carried in `brokerId` is a canonical adviser_id (or a
 * `lead_name:<name>` key for unmatched leads), never a raw dual-hash. This
 * guarantees the UI never renders a raw hash even on the degraded path.
 */
function brokerLabel(name: string | null | undefined, id: string): string {
  if (name && name.trim()) return name;
  if (id.startsWith('lead_name:')) {
    return id.slice('lead_name:'.length).trim() || 'Unmapped broker';
  }
  return 'Unmapped broker';
}

function conversionColor(pct: number): string {
  if (pct >= 20) return 'text-emerald-400';
  if (pct >= 10) return 'text-blue-400';
  if (pct >= 5) return 'text-amber-400';
  return 'text-gray-400';
}

interface BrokerSparklineProps {
  rows: WeeklyFunnelRow[];
}

/**
 * Per-broker sparkline. Renders one mini-bar per week showing the call
 * volume (largest, dimmer bar) and the settled count (smaller, brighter
 * bar) overlaid. Pure SVG — no chart library needed for ~12 bars.
 */
const BrokerSparkline: React.FC<BrokerSparklineProps> = ({ rows }) => {
  // Sort ascending for left→right time progression.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate)),
    [rows]
  );
  const maxCalls = useMemo(
    () => Math.max(1, ...sorted.map((r) => r.calls)),
    [sorted]
  );
  const maxSettled = useMemo(
    () => Math.max(1, ...sorted.map((r) => r.settled)),
    [sorted]
  );

  if (sorted.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic">No weekly activity recorded.</div>
    );
  }

  const width = Math.max(200, sorted.length * 28);
  const height = 60;
  const barWidth = 18;
  const gap = 10;

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height + 18} className="text-blue-400">
        {sorted.map((r, i) => {
          const x = i * (barWidth + gap);
          const callsH = (r.calls / maxCalls) * height;
          const settledH = (r.settled / maxSettled) * (height * 0.6);
          return (
            <g key={r.weekStartDate}>
              <rect
                x={x}
                y={height - callsH}
                width={barWidth}
                height={callsH}
                fill="currentColor"
                opacity={0.25}
                rx={2}
              />
              <rect
                x={x + barWidth / 4}
                y={height - settledH}
                width={barWidth / 2}
                height={settledH}
                fill="#10b981"
                rx={1}
              />
              <text
                x={x + barWidth / 2}
                y={height + 12}
                textAnchor="middle"
                fontSize={9}
                fill="#6b7280"
              >
                {formatWeekLabel(r.weekStartDate)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="mt-1 flex items-center gap-4 text-[10px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-blue-400/40 rounded-sm" /> Calls
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 bg-emerald-500 rounded-sm" /> Settled
        </span>
      </div>
    </div>
  );
};

const WeeklyFunnelPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  // Marketing can't connect CRM themselves, so reword empty-state copy
  // to point at an admin (marketing RBAC §6).
  const isMarketing = userData?.role === 'marketing';

  const [weeksBack, setWeeksBack] = useState<number>(12);
  const [selectedBrokerId, setSelectedBrokerId] = useState<string>('');

  const { data, loading, error, refresh } = useWeeklyFunnel({
    companyId,
    weeksBack,
  });

  const perBroker = data?.perBroker ?? [];
  const perWeek = data?.perWeek ?? [];
  const hasData = perBroker.length > 0;

  // Absent (older backend) is treated as fully covered, i.e. the prior render.
  const coverage = data?.stageCoverage;
  const covers = (stage: keyof NonNullable<typeof coverage>): boolean =>
    coverage ? coverage[stage] : true;

  const untrackedStages = useMemo(() => {
    if (!coverage) return [];
    return (
      [
        ['Calls', coverage.calls],
        ['Contacts', coverage.contacts],
        ['HelloPacks', coverage.hellopacks],
        ['Lodged', coverage.lodged],
        ['Settled', coverage.settled],
      ] as const
    )
      .filter(([, covered]) => !covered)
      .map(([label]) => label);
  }, [coverage]);

  // Totals across the entire window.
  const totals = useMemo(
    () =>
      perBroker.reduce(
        (acc, b) => ({
          calls: acc.calls + b.calls,
          contacts: acc.contacts + b.contacts,
          hellopacks: acc.hellopacks + b.hellopacks,
          lodged: acc.lodged + b.lodged,
          settled: acc.settled + b.settled,
        }),
        { calls: 0, contacts: 0, hellopacks: 0, lodged: 0, settled: 0 }
      ),
    [perBroker]
  );

  const overallConversionPct =
    totals.lodged > 0
      ? Math.round((totals.settled / totals.lodged) * 1000) / 10
      : 0;

  const sparklineRows = useMemo(
    () =>
      selectedBrokerId
        ? perWeek.filter((r) => r.brokerId === selectedBrokerId)
        : [],
    [perWeek, selectedBrokerId]
  );

  const selectedBrokerName = useMemo(() => {
    const match = perBroker.find((b) => b.brokerId === selectedBrokerId);
    return brokerLabel(match?.brokerName, selectedBrokerId);
  }, [perBroker, selectedBrokerId]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Weekly Funnel"
        subtitle={`Activity → Settlement conversion across the last ${weeksBack} weeks`}
        badge="Beta"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      <MappingIncompleteBanner />

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="text-gray-400">Window</label>
        <select
          value={weeksBack}
          onChange={(e) => setWeeksBack(Number(e.target.value))}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        >
          {WEEKS_OPTIONS.map((w) => (
            <option key={w} value={w}>
              Last {w} weeks
            </option>
          ))}
        </select>

        {hasData && (
          <>
            <label className="text-gray-400 ml-4">Broker (sparkline)</label>
            <select
              value={selectedBrokerId}
              onChange={(e) => setSelectedBrokerId(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm min-w-[12rem]"
            >
              <option value="">— Select to view trend —</option>
              {perBroker.map((b) => (
                <option key={b.brokerId} value={b.brokerId}>
                  {brokerLabel(b.brokerName, b.brokerId)}
                </option>
              ))}
            </select>
          </>
        )}

        {data?.rangeStart && data?.rangeEnd && (
          <span className="ml-auto text-xs text-gray-500">
            {formatWeekLabel(data.rangeStart)} → {formatWeekLabel(data.rangeEnd)}
          </span>
        )}
      </div>

      {/* Round-robin caveat (G6) */}
      <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 flex items-start gap-2 text-xs text-blue-200">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          Activities and HelloPacks are attributed to the broker who logged
          them. Tenants with shared inboxes or round-robin lead routing should
          read per-broker rows alongside their roster — the funnel does not
          redistribute across the team.
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Top-of-funnel KPI strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
        <FunnelStatCard
          icon={Phone}
          label="Calls"
          value={totals.calls}
          accent="border-t-blue-500 text-blue-400"
          covered={covers('calls')}
        />
        <FunnelStatCard
          icon={Users}
          label="Contacts"
          value={totals.contacts}
          accent="border-t-cyan-500 text-cyan-400"
          covered={covers('contacts')}
        />
        <FunnelStatCard
          icon={FileText}
          label="HelloPacks"
          value={totals.hellopacks}
          accent="border-t-purple-500 text-purple-400"
          covered={covers('hellopacks')}
        />
        <FunnelStatCard
          icon={Briefcase}
          label="Lodged"
          value={totals.lodged}
          accent="border-t-amber-500 text-amber-400"
          covered={covers('lodged')}
        />
        <FunnelStatCard
          icon={Handshake}
          label="Settled"
          value={totals.settled}
          accent="border-t-emerald-500 text-emerald-400"
          covered={covers('settled')}
          // Conversion divides settled by lodged; if either is unmeasured the
          // ratio is not a low number, it is not a number at all.
          footer={
            covers('settled') && covers('lodged')
              ? `Conversion: ${overallConversionPct}%`
              : undefined
          }
        />
      </div>

      {/* Gated on hasData: with no rows at all the "No funnel data yet" empty
          state below already explains the page, and showing both is noise. */}
      {hasData && untrackedStages.length > 0 && (
        <div className="rounded-md border border-gray-600/40 bg-gray-700/20 p-3 flex items-start gap-2 text-xs text-gray-300">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            {untrackedStages.join(', ')}{' '}
            {untrackedStages.length === 1 ? 'shows' : 'show'} {NOT_TRACKED} rather than 0:
            nothing has been recorded for them yet, so there is no number to
            report. A 0 would say these did not happen, which is a different
            claim from not having measured them.
          </div>
        </div>
      )}

      {!loading && !hasData && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <BarChart3 className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-white mb-2">
            No funnel data yet
          </h3>
          <p className="text-xs text-gray-400 max-w-md mx-auto">
            Log activities (calls, document sends)
            {isMarketing
              ? ' and ask an admin to connect your CRM'
              : ' and connect MyCRM'}{' '}
            so leads and deals flow into the warehouse. The funnel will populate
            as data arrives.
          </p>
        </div>
      )}

      {/* Per-broker table */}
      {hasData && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">
              Broker funnel ({perBroker.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/50 text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Broker</th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Phone className="inline w-3 h-3 mr-1" />Calls
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Users className="inline w-3 h-3 mr-1" />Contacts
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <FileText className="inline w-3 h-3 mr-1" />HelloPacks
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Briefcase className="inline w-3 h-3 mr-1" />Lodged
                  </th>
                  <th className="px-4 py-3 text-right font-medium">
                    <Handshake className="inline w-3 h-3 mr-1" />Settled
                  </th>
                  <th className="px-4 py-3 text-right font-medium">Conv %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {perBroker.map((b) => (
                  <tr
                    key={b.brokerId}
                    onClick={() => setSelectedBrokerId(b.brokerId)}
                    className={`cursor-pointer text-gray-200 hover:bg-white/[0.03] ${
                      selectedBrokerId === b.brokerId ? 'bg-blue-500/5' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-white">
                      {brokerLabel(b.brokerName, b.brokerId)}
                    </td>
                    <StatCell value={b.calls} covered={covers('calls')} />
                    <StatCell value={b.contacts} covered={covers('contacts')} />
                    <StatCell
                      value={b.hellopacks}
                      covered={covers('hellopacks')}
                    />
                    <StatCell value={b.lodged} covered={covers('lodged')} />
                    <StatCell
                      value={b.settled}
                      covered={covers('settled')}
                      className="text-emerald-400 font-semibold"
                    />
                    {covers('settled') && covers('lodged') ? (
                      <td
                        className={`px-4 py-3 text-right font-mono font-semibold ${conversionColor(b.conversionPct)}`}
                      >
                        {b.conversionPct}%
                      </td>
                    ) : (
                      <td
                        className="px-4 py-3 text-right font-mono text-gray-600"
                        title={NOT_TRACKED_HINT}
                      >
                        {NOT_TRACKED}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sparkline for selected broker */}
      {hasData && selectedBrokerId && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
          <h3 className="text-sm font-semibold text-white mb-3">
            {selectedBrokerName} — weekly trend
          </h3>
          <BrokerSparkline rows={sparklineRows} />
        </div>
      )}
    </div>
  );
};

interface StatCellProps {
  value: number;
  covered: boolean;
  className?: string;
}

/** One numeric table cell that renders NOT_TRACKED instead of an unjustified 0. */
const StatCell: React.FC<StatCellProps> = ({ value, covered, className = '' }) => (
  <td
    className={`px-4 py-3 text-right font-mono ${covered ? className : 'text-gray-600'}`}
    title={covered ? undefined : NOT_TRACKED_HINT}
  >
    {covered ? value.toLocaleString() : NOT_TRACKED}
  </td>
);

interface FunnelStatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  accent: string;
  footer?: string;
  /** false → this tenant does not record this stage; render NOT_TRACKED. */
  covered?: boolean;
}

const FunnelStatCard: React.FC<FunnelStatCardProps> = ({
  icon: Icon,
  label,
  value,
  accent,
  footer,
  covered = true,
}) => {
  const [borderClass, textClass] = accent.split(' ');
  return (
    <div
      className={`rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 ${borderClass} p-4`}
    >
      <div className="text-[11px] uppercase tracking-wider text-gray-400 inline-flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div
        className={`mt-1 text-3xl font-bold ${covered ? textClass : 'text-gray-600'}`}
        title={covered ? undefined : NOT_TRACKED_HINT}
      >
        {covered ? value.toLocaleString() : NOT_TRACKED}
      </div>
      {covered ? (
        footer && <div className="mt-1 text-[11px] text-gray-500">{footer}</div>
      ) : (
        <div className="mt-1 text-[11px] text-gray-500">Not tracked</div>
      )}
    </div>
  );
};

export default WeeklyFunnelPage;
